import React from "react";
import {
  Card,
  Button,
  Tag,
  Avatar,
  Empty,
  Divider,
  message,
  Row,
  Col,
} from "antd";
import { ShoppingCartOutlined } from "@ant-design/icons";
import { useCart } from "../../services/CartContext";
import { useValidateCart } from "../../hooks/useCartItems";
import { useNavigate } from "react-router-dom";
import "../../styles/cart.css";
import CartItem from "./CartItem";
import { FaArrowRight, FaShoppingBag, FaWhatsapp } from "react-icons/fa";

const Cart = ({ campaignCode }) => {
  const { cart, dispatch } = useCart();
  const navigate = useNavigate();

  const { mutateAsync: validateCartAsync, isLoading } = useValidateCart();

  const subtotal = cart.items.reduce(
    (sum, i) => sum + i.displayPrice * i.qty,
    0,
  );

  const FREE_DELIVERY_THRESHOLD = 3000;

  const remainingForFreeDelivery = Math.max(
    FREE_DELIVERY_THRESHOLD - subtotal,
    0,
  );

  const hasFreeDelivery = subtotal >= FREE_DELIVERY_THRESHOLD;

  const freeDeliveryProgress = Math.min(
    (subtotal / FREE_DELIVERY_THRESHOLD) * 100,
    100,
  );

  const handleCheckout = async () => {
    try {
      const result = await validateCartAsync(
        cart.items.map((i) => ({
          productId: i.productId,
          qty: i.qty,
        })),
      );

      if (!result.isValid) {
        message.error("Fix cart issues before checkout");
        return;
      }

      navigate("/checkout", { state: { campaignCode } });
    } catch {
      message.error("Validation failed");
    }
  };

  // --- WhatsApp Order Handler ---
  const handleSendToWhatsApp = () => {
    const phoneNumber = "254116088688"; // Format: country code without '+'

    // Format item list
    const itemsList = cart.items
      .map((item, index) => {
        const unit = item.displayUnitName ? ` (${item.displayUnitName})` : "";
        const itemTotal = (item.displayPrice * item.qty).toLocaleString();
        return `${index + 1}. *${item.title}*${unit}\n   Qty: ${item.qty} x KES ${item.displayPrice.toLocaleString()} = *KES ${itemTotal}*`;
      })
      .join("\n\n");

    const deliveryStatus = hasFreeDelivery
      ? "Qualifies for Free Delivery 🎉"
      : `KES ${remainingForFreeDelivery.toLocaleString()} away from Free Delivery`;

    // Compose message body
    const textMessage = `🛒 *NEW ORDER REQUEST*
----------------------------------
${itemsList}

----------------------------------
💰 *Subtotal:* KES ${subtotal.toLocaleString()}
🚚 *Delivery Status:* ${deliveryStatus}
${campaignCode ? `🏷️ *Campaign Code:* ${campaignCode}\n` : ""}
Please assist me in completing this order.`;

    // Encode for URL & open WhatsApp API link
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(textMessage)}`;
    window.open(whatsappUrl, "_blank");
  };

  const onBackToBrowse = () => {
    if (campaignCode) {
      navigate(`/${campaignCode}`);
    } else {
      navigate("/"); // fallback if code is missing
    }
  };

  if (!cart.items.length) {
    return (
      <div className="cart-page">
        <Card className="cart-empty-card">
          <Empty description="Cart is empty" />
          <Button type="primary" onClick={onBackToBrowse}>
            Continue Shopping
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="cart-page">
      {/* HEADER */}
<Card className="cart-user-card">
  <div className="cart-user-content">
    <div className="cart-user-left">
      <Avatar
        icon={<ShoppingCartOutlined />}
        style={{ backgroundColor: "#1890ff", color: "#fff" }}
      />
      <div>
        <h3>Shopping Cart</h3>
        <Tag color="blue">{cart.items.length} items</Tag>
      </div>
    </div>
    <Button type="primary" onClick={onBackToBrowse}>
      Back to Catalogue
    </Button>
  </div>
</Card>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Card
            className="cart-main-card"
            title={
              <div className="card-title-flex">
                <div className="card-title-icon">
                  <ShoppingCartOutlined />
                  <span>Shopping Cart</span>
                </div>

                <Tag color="blue">{cart.items.length} Items</Tag>
              </div>
            }
          >
            <div className="cart-items-list">
              {cart.items.map((item) => (
                <CartItem
                  key={item.productId}
                  item={item}
                  dispatch={dispatch}
                />
              ))}
            </div>
          </Card>
        </Col>

        {/* RIGHT */}
        <Col xs={24} lg={8}>
          <div className="cart-summary-wrapper">
            <Card className="cart-summary-card">
              <div className="summary-header">
                <FaShoppingBag />
                <h3>Order Summary</h3>
              </div>

              <Divider />

              <div className="summary-row">
                <span>Subtotal</span>
                <strong>KES {subtotal.toLocaleString()}</strong>
              </div>

              <div
                className={`free-delivery-card ${
                  hasFreeDelivery ? "success" : "pending"
                }`}
              >
                <div className="free-delivery-header">
                  <span>
                    {hasFreeDelivery
                      ? "🎉 You qualify for 100% Discount FREE delivery"
                      : "🚚 100% Discount Free delivery available"}
                  </span>

                  <strong>{Math.round(freeDeliveryProgress)}%</strong>
                </div>

                <div className="free-delivery-progress">
                  <div
                    className="free-delivery-progress-bar"
                    style={{ width: `${freeDeliveryProgress}%` }}
                  />
                </div>

                <p>
                  {hasFreeDelivery ? (
                    <>
                      Your order qualifies for 100% Discount delivery at
                      checkout.
                    </>
                  ) : (
                    <>
                      Add items worth{" "}
                      <strong>
                        KES {remainingForFreeDelivery.toLocaleString()}
                      </strong>{" "}
                      more to unlock free delivery.
                    </>
                  )}
                </p>
              </div>

              <div className="summary-row muted">
                Delivery calculated at checkout
              </div>

              <Divider />

              <div className="summary-total">
                <span>Estimated Total</span>
                <h2>KES {subtotal.toLocaleString()}</h2>
              </div>

              {/* STANDARD CHECKOUT */}
              <Button
                type="primary"
                size="large"
                block
                loading={isLoading}
                icon={<FaArrowRight />}
                onClick={async () => {
                  try {
                    const result = await validateCartAsync(
                      cart.items.map((item) => ({
                        productId: item.productId,
                        qty: item.qty,
                      })),
                    );

                    if (result.isValid) {
                      handleCheckout();
                    } else {
                      message.error("Please fix cart issues before checkout.");
                    }
                  } catch {
                    message.error("Validation failed");
                  }
                }}
              >
                Proceed To Checkout
              </Button>

              {/* WHATSAPP CUSTOMER CARE BUTTON */}
              <Button
                size="large"
                block
                style={{
                  marginTop: 12,
                  backgroundColor: "#25D366",
                  borderColor: "#25D366",
                  color: "#fff",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
                onClick={handleSendToWhatsApp}
              >
                <FaWhatsapp style={{ fontSize: "20px" }} />
                Order via WhatsApp
              </Button>

              <Button
                size="large"
                block
                style={{ marginTop: 12 }}
                onClick={onBackToBrowse}
              >
                Continue Shopping
              </Button>
            </Card>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Cart;
